import {useMemo, useState} from "react";
import DispatchInfoService from "../../../../../../services/reports/dispatch-info-service.js";
import ModalAttaches from "../../../../../../components/modal/modal-attaches.jsx";
import InfoGrid from "../../../../../../components/info-grid/info-grid.jsx";
import ReportUtils from "../../../../../../utils/report-utils.js";

const imgExtensions = ["jpg", "jpeg", "png", "gif", "svg", "image/jpg", "image/jpeg", "image/png", "image/gif", "image/svg+xml",];

export default function Attachments({ title, data, printOptions }) {
    const attaches = data?.dispatch?.attachment ?? [];
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const openAttach = (attach) => {
        setSelectedFile({
            ...attach,
            attachId: attach.id,
            url: DispatchInfoService.getAttach(attach.id),
            extension: attach.type ?? attach.name?.split(".").pop(),
            type: attach.type ?? attach.name?.split(".").pop(),
        });

        setModalOpen(true);
    }

    const getFileExtension = (attach) => {
        return attach?.name?.split(".").pop()?.toLowerCase() ?? "";
    };

    const getExtension = (attach) => {
        return String(attach?.type ?? getFileExtension(attach)).toLowerCase();
    };

    const isImageAttach = (attach) => {
        return imgExtensions.includes(getExtension(attach));
    };

    const getPreviewUrl = (attach) => {
        const isImage = isImageAttach(attach);
        const fileExtension = getFileExtension(attach);

        return isImage
            ? DispatchInfoService.getAttach(attach.id)
            : DispatchInfoService.getImage(fileExtension);
    };

    const imageAttaches = useMemo(() => {
        return attaches.filter(isImageAttach)
    }, [attaches]);

    const nonImageAttaches = useMemo(() => {
        return attaches.filter((attach) => !isImageAttach(attach))
    }, [attaches]);

    const attachItems = useMemo(() => {
        return attaches.map((attach) => ({
            label: "Arquivo",
            value: attach.name,
        }));
    }, [attaches]);

    const nonImageAttachItems = useMemo(() => {
        return nonImageAttaches.map((attach) => ({
            label: "Arquivo",
            value: attach.name,
        }));
    }, [nonImageAttaches]);


    const showImages = !!printOptions.showImages;
    const attachFullSize = !!printOptions.attachFullSize;

    return (
        <div className={`
            px-5 space-y-4
            ${attachFullSize ? "print:px-0 print:space-y-0" : ""}
            ${!showImages ? "print:hidden" : ""}
        `}>
            <h2
                className={`
                    w-full rounded-[5px] border border-[#e0e0e0] bg-[#efefef] py-0! text-center text-lg! text-[#4f4f4f]
                    ${attachFullSize ? "print:hidden" : ""}
                `}
            >
                {title}
            </h2>
            <section className="print:hidden grid grid-cols-4 gap-3">
                { attaches.map((attach) => {
                    const previewUrl = getPreviewUrl(attach);

                    return (
                        <div key={attach.id}>
                            <div className="flex h-[204px] w-full items-center justify-center rounded border border-[#e0e0e0]">
                                <img
                                    src={previewUrl}
                                    alt={attach.name}
                                    title={attach.name}
                                    onClick={() => openAttach(attach)}
                                    className="max-h-[180px] cursor-pointer"
                                />
                            </div>
                            <p className="info text-center mt-2 break-all!">
                                {attach.description}
                            </p>
                        </div>
                    )
                })}
            </section>

            {/* IMPRESSÃO */}
            { !showImages && (
                <div className="hidden print:block">
                    <InfoGrid
                        items={attachItems}
                        columns={1}
                        className="px-0!"
                        contentClassName="gap-1"
                    />
                </div>
            )}

            {/* IMPRESSÃO: showImages true, mas attachFullSize false */}
            { showImages && !attachFullSize && (
                <section className="hidden print:grid print:grid-cols-4 print:gap-3">
                    {attaches.map((attach) => {
                        const previewUrl = getPreviewUrl(attach);

                        return (
                            <div key={attach.id} className="print:break-inside-avoid">
                                <div className="flex h-[204px] w-full items-center justify-center rounded border border-[#e0e0e0]">
                                    <img
                                        src={previewUrl}
                                        alt={attach.name}
                                        title={attach.name}
                                        className="max-h-[180px]"
                                    />
                                </div>
                                <p className="info text-center break-all!">
                                    {attach.description}
                                </p>
                            </div>
                        )
                    })}
                </section>
            )}

            {/* IMPRESSÃO: attachFullSize true */}
            { showImages && attachFullSize && (
                <section className="hidden print:block print:m-0 print:p-0">
                    { nonImageAttaches.length > 0 && (
                        <div className="print:mb-6 print:px-5!">
                            <InfoGrid
                                items={nonImageAttachItems}
                                columns={1}
                                className="px-0!"
                                contentClassName="gap-1"
                            />
                        </div>
                    )}

                    { imageAttaches.map((attach, index) => {
                        const previewUrl = getPreviewUrl(attach);
                        const attachTitle = attach.description || attach.name || title;

                        return (
                            <div
                                key={attach.id}
                                className={`
                                    print:block
                                    print:w-full
                                    print:h-[250mm]
                                    print:m-0
                                    print:p-0
                                    print:overflow-hidden
                                    print:break-inside-avoid
                                    ${index > 0 ? "print:break-before-page" : ""}
                                `}
                            >
                                <h2
                                    className="
                                        print:block
                                        print:m-0
                                        print:mb-[3mm]
                                        print:px-2
                                        print:py-1
                                        w-full
                                        h-auto
                                        rounded-[5px]
                                        border
                                        border-[#e0e0e0]
                                        bg-[#efefef]
                                        text-center
                                        text-lg!
                                        leading-snug
                                        text-[#4f4f4f]
                                        overflow-hidden
                                        [display:-webkit-box]
                                        [-webkit-box-orient:vertical]
                                        px-5!
                                        break-all!
                                    "
                                >
                                    {ReportUtils.textLimit(attachTitle, 375)}
                                </h2>

                                <div
                                    className="
                                        print:block
                                        print:w-full
                                        print:h-[237mm]
                                        print:m-0
                                        print:p-0
                                        print:text-center
                                        print:overflow-hidden
                                    "
                                >
                                    <img
                                        src={previewUrl}
                                        alt={attach.name}
                                        className="
                                            print:block
                                            print:w-full
                                            print:h-full
                                            print:object-contain
                                        "
                                    />
                                </div>
                            </div>
                        );
                    })}
                </section>
            )}

            <ModalAttaches
                open={modalOpen}
                setOpen={setModalOpen}
                file={selectedFile}
            />
        </div>
    )
}